import { CORPORATE_CLIENT_COUNTRIES } from 'constants/corporateClients';

// Single source of truth for a corporation's country -> currency. The symbol (shown beside
// amounts) and the ISO code (expected by the shared formatCurrency helper in
// src/pages/InvoicesAndStatements/helper) live together so the two can't drift apart.
const COUNTRY_CURRENCY = {
  [CORPORATE_CLIENT_COUNTRIES.US]: { code: 'USD', symbol: '$' },
  [CORPORATE_CLIENT_COUNTRIES.CA]: { code: 'CAD', symbol: '$' },
  [CORPORATE_CLIENT_COUNTRIES.GB]: { code: 'GBP', symbol: '£' },
  [CORPORATE_CLIENT_COUNTRIES.FR]: { code: 'EUR', symbol: '€' },
  [CORPORATE_CLIENT_COUNTRIES.DE]: { code: 'EUR', symbol: '€' },
  [CORPORATE_CLIENT_COUNTRIES.CH]: { code: 'CHF', symbol: 'CHF' },
};

// GB stands in for an unknown or missing country.
const DEFAULT_CURRENCY = COUNTRY_CURRENCY[CORPORATE_CLIENT_COUNTRIES.GB];

export const getCurrency = country => COUNTRY_CURRENCY[country] || DEFAULT_CURRENCY;

export const getCurrencySymbol = country => getCurrency(country).symbol;

export const getCurrencyCode = country => getCurrency(country).code;

// Number formatting follows the currency's home market: GBP as "£1,234.50" (symbol first, comma
// thousands, dot decimals), EUR French-style as "1 234,50 €" (symbol last after a no-break space,
// narrow no-break space thousands, comma decimals).
const CURRENCY_LOCALES = {
  GBP: 'en-GB',
  EUR: 'fr-FR',
  USD: 'en-US',
  CAD: 'en-CA',
  CHF: 'de-CH',
};

// Formats a major-unit amount in an ISO 4217 currency (any case). `wholeUnits` drops the decimals
// for whole amounts ("£1,000", but still "£12.50"); `signDisplay` is Intl's option of that name
// ('exceptZero' gives "+£12.50"); `showSymbol: false` keeps the currency's number format but drops
// the symbol ("1 234,50" for EUR). Returns '' for a missing or non-numeric amount, and just the
// formatted number when the currency is unknown.
export const formatCurrencyAmount = (
  amount,
  currencyCode,
  { wholeUnits = false, signDisplay = 'auto', showSymbol = true } = {},
) => {
  if (amount === null || amount === undefined || amount === '' || Number.isNaN(Number(amount))) {
    return '';
  }

  const value = Number(amount);
  const code = String(currencyCode ?? '').toUpperCase();
  const locale = CURRENCY_LOCALES[code];
  const fractionDigits = wholeUnits && Number.isInteger(value) ? 0 : 2;
  const options = {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay,
  };

  if (!locale || !showSymbol) {
    return new Intl.NumberFormat(locale ?? 'en-GB', options).format(value);
  }
  return new Intl.NumberFormat(locale, { ...options, style: 'currency', currency: code }).format(
    value,
  );
};
